/**
 * Common Email Validator Utility
 * Provides consistent email validation across the application
 */

/**
 * Standard email validation pattern
 * This pattern validates most common email formats while being reasonably permissive
 */
const EMAIL_PATTERN =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Comprehensive email validation pattern
 * More strict validation that handles edge cases and special characters
 */
const COMPREHENSIVE_EMAIL_PATTERN =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * Validates an email address using the standard pattern
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if email is valid, false otherwise
 */
export function isValidEmail(email) {
    if (!email || typeof email !== "string") {
        return false;
    }
    return EMAIL_PATTERN.test(email.trim());
}

/**
 * Validates an email address using the comprehensive pattern
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if email is valid, false otherwise
 */
export function isValidEmailComprehensive(email) {
    if (!email || typeof email !== "string") {
        return false;
    }
    return COMPREHENSIVE_EMAIL_PATTERN.test(email.trim());
}

/**
 * Validates multiple comma-separated email addresses
 * @param {string} emails - Comma-separated email addresses
 * @returns {boolean} - True if all emails are valid, false otherwise
 */
export function isValidEmailList(emails) {
    if (!emails || typeof emails !== "string") {
        return false;
    }

    const emailList = emails
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

    if (emailList.length === 0) {
        return false;
    }

    return emailList.every((email) => isValidEmail(email));
}

/**
 * Gets the appropriate validation icon based on email validity
 * @param {string} email - The email address
 * @param {boolean} isEmpty - Whether the email field is empty
 * @returns {string} - Icon name for the validation state
 */
export function getEmailValidationIcon(email, isEmpty = false) {
    if (isEmpty || !email || email.trim() === "") {
        return "mail";
    }

    if (isValidEmail(email)) {
        return "task_alt";
    }

    return "highlight_off";
}

/**
 * Validates email and returns validation result object
 * @param {string} email - The email address to validate
 * @returns {object} - Object containing validation status and message
 */
export function validateEmailWithMessage(email) {
    if (!email || email.trim() === "") {
        return {
            isValid: false,
            message: "Email is required",
            icon: "mail",
        };
    }

    if (isValidEmail(email)) {
        return {
            isValid: true,
            message: "",
            icon: "task_alt",
        };
    }

    return {
        isValid: false,
        message: "Please enter a valid email address",
        icon: "highlight_off",
    };
}

// Export the patterns for use in other utilities if needed
export { EMAIL_PATTERN, COMPREHENSIVE_EMAIL_PATTERN };
